import { inject, Provider, Type } from '@angular/core';
import { GridApi, RowClickedEvent } from 'ag-grid-community';
import { BehaviorSubject, defer, Observable, shareReplay } from 'rxjs';
import { JsonStorageService } from './json-storage.service';
import { ListViewDataBase } from './list-view.model';
import { QuickFilterModel } from './model/quick-filter.model';

export abstract class AbstractSimpleListViewService<Model = ListViewDataBase> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected constructor(protected readonly store?: any) {}
  abstract readonly data$: Observable<Model[]>;
  abstract readonly loading$: Observable<boolean>;

  readonly items$ = defer(() => this.data$).pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private readonly activeItem$$ = new BehaviorSubject<Model | null>(null);
  readonly activeItem$ = this.activeItem$$.asObservable();

  private _gridApi!: GridApi;
  get gridApi() {
    return this._gridApi;
  }

  getAll?(): Promise<void>;
  add?(): Promise<void>;
  edit?(rowClickedEvent: RowClickedEvent & { data: Model }): Promise<void>;
  delete?(): Promise<void>;

  setActiveItem(item: Model | null) {
    this.activeItem$$.next(item);
  }

  /**
   * Sets the ag-grid `GridApi` object in the service.
   * Called by the `list-view-grid.component`.
   */
  setGridApi(gridApi: GridApi) {
    this._gridApi = gridApi;
  }
}

export abstract class AbstractListViewService<
  Model extends ListViewDataBase = ListViewDataBase,
> extends AbstractSimpleListViewService<Model> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(protected readonly store?: any) {
    super(store);
  }

  private readonly jsonStorage = inject(JsonStorageService);

  getQuickFilters(id: string): Observable<QuickFilterModel[]> {
    return this.jsonStorage.getStorageByKey(id);
  }

  addQuickFilter(filter: QuickFilterModel, location: string): Observable<QuickFilterModel[]> {
    return this.jsonStorage.addQuickFilter(filter, location);
  }

  updateQuickFilter(id: string, filter: QuickFilterModel, location: string): Observable<QuickFilterModel[]> {
    return this.jsonStorage.updateQuickFilter(id, filter, location);
  }

  deleteQuickFilter(id: string, location: string): Observable<QuickFilterModel[]> {
    return this.jsonStorage.deleteQuickFilter(id, location);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function provideSimpleListView(listViewService: Type<AbstractSimpleListViewService<any>>): Provider[] {
  return [
    listViewService,
    {
      provide: AbstractListViewService,
      useExisting: listViewService,
    },
  ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function provideListView(listViewService: Type<AbstractListViewService<any>>): Provider[] {
  return [
    listViewService,
    {
      provide: AbstractListViewService,
      useExisting: listViewService,
    },
  ];
}
